import { api } from "./api";
import type { Units } from "@stores/SettingsStore";

export type DashboardDevice = {
  esn: string;
  attachmentId: string;
  name: string;
  width: number;
  totalAreaHa: number;
};

export type DailyArea = {
  device_id: string;
  day: string;
  area_ha: number;
};

export type HarvestedAreaResponse = {
  devices: DashboardDevice[];
  daily: DailyArea[];
};

export const getHarvestedArea = async (
  from: string,
  to: string,
  insideFieldsOnly?: boolean
): Promise<HarvestedAreaResponse> => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return api.get<HarvestedAreaResponse>("/api/v1/dashboard/harvested-area", {
    from,
    to,
    tz,
    // Left undefined when off so the URL stays exactly as it was before this
    // option existed (buildQueryString drops undefined).
    insideFieldsOnly: insideFieldsOnly ? "true" : undefined,
  });
};

// ── Average yield ─────────────────────────────────────────────

export type YieldDevice = {
  esn: string;
  name: string;
  avgYield: number;
  /** Season total mass in the user's weight unit (sensor path + calibration). */
  totalMass: number;
  totalHarvestHours: number;
  /** "johndeere": a John Deere combine without a GreenGrowth calibration. */
  calCoeffSource: "calibration" | "fallback" | "johndeere";
};

export type DailyYield = {
  device_id: string;
  day: string;
  yield_value: number;
  /** Daily total mass in the user's weight unit (same integration as yield). */
  mass_value: number;
  /** Tons per hour for the day. */
  tph_value: number;
};

export type AverageYieldResponse = {
  devices: YieldDevice[];
  daily: DailyYield[];
  unit: string; // e.g. "t/ha"
  tphUnit: string; // weight unit per hour, e.g. "t/h", "kg/h"
};

export const getAverageYield = async (
  from: string,
  to: string,
  units: Units,
  insideFieldsOnly?: boolean
): Promise<AverageYieldResponse> => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return api.get<AverageYieldResponse>("/api/v1/dashboard/average-yield", {
    from,
    to,
    tz,
    weight: units.weight,
    area: units.area,
    poundsPerBushel: String(units.poundsPerBushel),
    poundsPerBag: String(units.poundsPerBag),
    insideFieldsOnly: insideFieldsOnly ? "true" : undefined,
  });
};
